import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { LoyaltyPointsRangesToken } from '@sleed/bizcommerce-api';
import { pointRangesApi } from '~/lib/api/client';
import { formToToken, type LoyaltyRangeFormValues } from '~/lib/schemas/loyalty-range';

export const loyaltyRangeKeys = {
	all: ['loyalty-ranges'] as const,
	list: () => [...loyaltyRangeKeys.all, 'list'] as const
};

export function useLoyaltyRangesList() {
	return useQuery<LoyaltyPointsRangesToken[]>({
		queryKey: loyaltyRangeKeys.list(),
		queryFn: async () => (await pointRangesApi.getAll()).data.items ?? []
	});
}

export function useLoyaltyRangeMutations() {
	const qc = useQueryClient();
	const invalidate = () => qc.invalidateQueries({ queryKey: loyaltyRangeKeys.list() });
	const create = useMutation({
		mutationFn: async (form: LoyaltyRangeFormValues) => (await pointRangesApi.insert(formToToken(form))).data,
		onSuccess: invalidate
	});
	const update = useMutation({
		mutationFn: async (form: LoyaltyRangeFormValues) => (await pointRangesApi.update(formToToken(form))).data,
		onSuccess: invalidate
	});
	// SoftDelete returns a status string ('already_cancelled' | 'expired_successfully' | 'deleted_sucessfully')
	// that the caller surfaces as a toast (legacy parity).
	const remove = useMutation({
		mutationFn: async (id: number) => (await pointRangesApi.softDelete(id)).data,
		onSuccess: invalidate
	});
	return { create, update, remove };
}
